import { defineComponent as _defineComponent } from 'vue'
import { resolveDynamicComponent as _resolveDynamicComponent, toHandlers as _toHandlers, mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock, KeepAlive as _KeepAlive, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  id: "modals-container-scroll-helper",
  class: "modals-container__status-bar-wrapper"
}
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "modals-container__status-bar-safe-area" }, null, -1)

import { isEmpty } from 'lodash/fp';
import { nanoid } from 'nanoid';
import { computed, ref, type ShallowRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { RouteLocationNormalized } from 'vue-router';

import { authService } from '@exchange/libs/auth/service/src';
import { capIsNativePlatform } from '@exchange/libs/utils/capacitor/src';
import { checkIsBiometricRoute, getNoAuthRequiredForRoute } from '@exchange/routing';

import { modalVariant } from './modal-type-validators';
import modalService, { type ModalToShow, type ModalToShowOptions } from './modals.service';

const PREFIX = 'modal-';


export default /*#__PURE__*/_defineComponent({
  __name: 'ModalsContainer',
  setup(__props, { expose: __expose }) {

function getComponentProp<T>(comp: ShallowRef, prop: string, defaultVal: T): T {
  return comp[prop] || defaultVal;
}

const modalStack = ref<Array<ModalToShow>>([]);
const container = ref<HTMLElement>();
const centeredModal = ref(false);
const variant = ref(modalVariant.dark);

const route = useRoute();
const router = useRouter();

const isNativePlatform = capIsNativePlatform();

const currentModal = computed(() => (modalStack.value.length ? modalStack.value[modalStack.value.length - 1] : undefined));

const shouldHideCurrentModal = computed(() => currentModal.value && currentModal.value.isPrivate && authService.initialLoadAuthIsChecked && !authService.hasBeenAuthenticated);

const isBiometricRoute = computed(() => checkIsBiometricRoute(route));

const add = ({ component, props, listeners, compOptions }) => {
  const key = `${PREFIX}${nanoid()}`;
  const privateComponent = getComponentProp<boolean>(component, 'isPrivate', compOptions.isPrivate);
  const canCloseComponent = getComponentProp<boolean>(component, 'canClose', compOptions.canClose);

  modalStack.value = [
    ...modalStack.value,
    {
      key,
      component,
      props,
      listeners,
      isPrivate: privateComponent,
      canClose: canCloseComponent,
    },
  ];

  centeredModal.value = compOptions.centered;
  variant.value = compOptions.variant ?? modalVariant.dark;

  return key;
};

const defaultModalToShowOption: ModalToShowOptions = {
  isPrivate: false,
  canClose: true,
  variant: modalVariant.dark,
  centered: false,
};

const show = (component: ShallowRef, props = {}, listeners = {}, compOptions: ModalToShowOptions = defaultModalToShowOption) =>
  add({
    component,
    props,
    listeners,
    compOptions,
  });

const substitute = (component: ShallowRef, props = {}, listeners = {}, compOptions: ModalToShowOptions = defaultModalToShowOption) => {
  modalStack.value.pop();

  return add({
    component,
    props,
    listeners,
    compOptions,
  });
};

const hide = (modalKey?: string) => {
  if (modalKey) {
    modalStack.value = modalStack.value.filter(({ key }) => key !== modalKey);
  } else {
    modalStack.value = modalStack.value.slice(0, -1);
  }
};

const clear = () => {
  modalStack.value = [];
};

const outsideClick = () => {
  /**
   * Handles click events outside an element.
   * If modal dialog component - `currentModal` - has `modals-container-on-outside-click` listener it will be called before closing the modal dialog.
   *
   */

  if (
    currentModal.value &&
    currentModal.value.listeners &&
    currentModal.value.listeners['modals-container-on-outside-click'] &&
    typeof currentModal.value.listeners['modals-container-on-outside-click'] === 'function'
  ) {
    currentModal.value.listeners['modals-container-on-outside-click']();
  }

  hide();
};

const onMouseDown = (e: MouseEvent) => {
  if (currentModal.value?.canClose && container.value && e.target === container.value) {
    outsideClick();
  }
};

const announceModalClosed = (name: string | undefined) => {
  const event = new CustomEvent(`modal-closed:${name}`);

  container.value?.dispatchEvent(event);
};

watch(shouldHideCurrentModal, (value) => {
  if (value) {
    hide(currentModal.value?.key);
  }
});

watch(modalStack, () => {
  const { length } = modalStack.value;

  if (length === 0 && !isEmpty(route.hash)) {
    router.push({
      name: route.name as string,
      query: route.query,
      params: route.params,
    });
  }
});

watch(
  () => route.hash,
  async (newHash, oldHash) => {
    const wasShown = modalService.registeredModals.find((m) => `#${m.name}` === oldHash);
    const modalToShow = modalService.registeredModals.find((m) => `#${m.name}` === newHash);
    const currentModalKey = currentModal.value ? currentModal.value.key : undefined;

    if (!newHash && wasShown) {
      announceModalClosed(oldHash);

      // hash was removed but modal that was opened with hash is still shown
      if (currentModalKey) {
        hide(currentModalKey);

        return;
      }
    }

    // modal based on provided hash was not found
    if (!modalToShow) {
      return;
    }

    // if direct link was used we need to wait for router middleware check
    if (!authService.initialLoadAuthIsChecked) {
      await authService.waitForAuthIsChecked();
    }

    const noAuthRequired = getNoAuthRequiredForRoute({
      ...route,
      // both - the route, and modal need to be Public
      meta: { isPublic: !modalToShow.options?.isPrivate && route.meta?.isPublic },
    } as RouteLocationNormalized);

    if (!(await authService.checkRouteAccess({ noAuthRequired, isNativePlatform, trackingId: `ModalsContainer_${newHash}` }))) {
      // auth redirect will take us to login
      return;
    }

    // modal was fond and router access was granted
    show(
      modalToShow.component,
      {},
      {},
      {
        isPrivate: Boolean(modalToShow.options?.isPrivate),
        canClose: Boolean(modalToShow.options?.canClose),
        variant: modalVariant.dark,
      },
    );
  },
  { deep: false, immediate: true },
);

__expose({
  clear,
  hide,
  show,
  substitute,
});

return (_ctx: any,_cache: any) => {
  return (!isBiometricRoute.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        ref_key: "container",
        ref: container,
        class: _normalizeClass(['modals-container', `modals-container--${variant.value}`, { 'modals-container--click-through': !currentModal.value, 'modals-container--centered': centeredModal.value }]),
        onMousedown: onMouseDown
      }, [
        _createVNode(_Transition, {
          name: "modals-fade",
          mode: "out-in"
        }, {
          default: _withCtx(() => [
            (currentModal.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                  (_openBlock(), _createBlock(_KeepAlive, null, [
                    (_openBlock(), _createBlock(_resolveDynamicComponent(currentModal.value.component), _mergeProps({
                      key: currentModal.value.key,
                      class: "modals-container__status-bar-modal-content"
                    }, currentModal.value.props, _toHandlers(currentModal.value.listeners)), null, 16))
                  ], 1024)),
                  _hoisted_2
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ], 34))
    : _createCommentVNode("", true)
}
}

})